import React, { useState, useRef, useCallback } from 'react';
import WavesurferPlayer from '@wavesurfer/react';

const CartesiaVoice = ({ voiceId, previewUrl, authToken }) => {
  const [wavesurfer, setWavesurfer]     = useState(null);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [isReady, setIsReady]           = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError]               = useState(null);
  const [audioUrl, setAudioUrl]         = useState(null);
  const [showPlayer, setShowPlayer]     = useState(false);

  const blobRef = useRef(null);
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handlePreview = useCallback(async () => {
    setIsGenerating(true);
    setError(null);

    try {
      let fetchUrl;

      if (previewUrl) {
        // ── Use proxy to fetch Cartesia preview with API key ──
        // Direct URL fails with 401 because browser can't send
        // Authorization header for audio src requests
        fetchUrl = `${apiBase}/cartesia/voices/preview-audio?url=${encodeURIComponent(previewUrl)}`;
      } else {
        // ── No preview URL — generate via TTS ─────────────────
        fetchUrl = `${apiBase}/cartesia/tts/preview`;
      }

      const response = await fetch(fetchUrl, {
        method: previewUrl ? 'GET' : 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          ...(!previewUrl && { 'Content-Type': 'application/json' }),
        },
        ...(!previewUrl && {
          body: JSON.stringify({ voice_id: voiceId }),
        }),
      });

      if (!response.ok) throw new Error(`Server error ${response.status}`);

      const blob = await response.blob();
      if (!blob || blob.size === 0) throw new Error('Empty audio response');

      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
      const objUrl = URL.createObjectURL(blob);
      blobRef.current = objUrl;

      setAudioUrl(objUrl);
      setTimeout(() => setShowPlayer(true), 0);

    } catch (err) {
      console.error('Cartesia preview failed:', err);
      setError(err.message || 'Preview failed');
    } finally {
      setIsGenerating(false);
    }
  }, [voiceId, previewUrl, authToken, apiBase]);

  const onReady = useCallback((ws) => {
    setWavesurfer(ws);
    setIsReady(true);
    setIsPlaying(false);
    setTimeout(() => { try { ws.play(); } catch (e) {} }, 150);
  }, []);

  const onPlay   = useCallback(() => setIsPlaying(true),  []);
  const onPause  = useCallback(() => setIsPlaying(false), []);
  const onFinish = useCallback(() => setIsPlaying(false), []);

  const handlePlayPause = useCallback(() => {
    if (wavesurfer && isReady) wavesurfer.playPause();
  }, [wavesurfer, isReady]);

  React.useEffect(() => {
    return () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); };
  }, []);

  if (error) {
    return (
      <div className="voice-error">
        {error}
        <button
          onClick={() => { setError(null); handlePreview(); }}
          style={{
            background: 'none', border: 'none', color: 'inherit',
            textDecoration: 'underline', cursor: 'pointer', padding: '0 0 0 8px',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!showPlayer) {
    return (
      <button
        className="voice-preview-trigger"
        onClick={handlePreview}
        disabled={isGenerating}
        style={{ width: '100%', marginTop: '8px' }}
      >
        {isGenerating
          ? <><span className="voice-spinner" /> Loading…</>
          : '▶ Preview'
        }
      </button>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <WavesurferPlayer
        height={70}
        waveColor="rgba(124, 58, 237, 0.3)"
        progressColor="#7c3aed"
        cursorColor="#a78bfa"
        barWidth={2}
        barRadius={2}
        url={audioUrl}
        onReady={onReady}
        onPlay={onPlay}
        onPause={onPause}
        onFinish={onFinish}
      />
      <button
        className={`voice-preview-trigger voice-preview-trigger--play${!isReady ? ' voice-preview-trigger--loading' : ''}`}
        onClick={handlePlayPause}
        disabled={!isReady}
        style={{ width: '100%', marginTop: '8px' }}
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
      >
        {!isReady
          ? <><span className="voice-spinner" /> Loading…</>
          : isPlaying ? '⏸ Pause' : '▶ Play'
        }
      </button>
    </div>
  );
};

export default CartesiaVoice;